import React, { useState } from "react";
import { Instagram, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, useSEO } from "@/lib/api";
import { MaskReveal } from "@/components/Reveal";

export default function Contact() {
  useSEO("Contact", "Get in touch with VYRON — luxury eyewear concierge in Chandigarh, Mohali, India.");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill all required fields.");
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const field = "w-full bg-[#F5F5F5] rounded-2xl px-5 py-4 text-sm outline-none focus:ring-1 ring-black transition";

  return (
    <div data-testid="contact-page" className="pt-32 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-5">Concierge</p>
          <h1 className="font-display text-6xl md:text-8xl tracking-[-0.03em] leading-[0.85] mb-10">
            <MaskReveal>Let&rsquo;s talk.</MaskReveal>
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-md mb-10">
            Questions on fit, orders, or a bespoke request? Our concierge team responds within one business day.
          </p>
          <div className="space-y-5">
            <div className="flex items-center gap-4"><Mail size={18} /> <span className="text-sm">vyronofficial6@gmail.com</span></div>
            <div className="flex items-center gap-4"><MapPin size={18} /> <span className="text-sm">VYRON, Chandigarh, Mohali, India</span></div>
            <a href="https://www.instagram.com/wear.vyronofficial/" target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 link-underline w-fit">
              <Instagram size={18} /> <span className="text-sm">@wear.vyronofficial</span>
            </a>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid="contact-name" placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
              <input data-testid="contact-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
            </div>
            <input data-testid="contact-subject" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={field} />
            <textarea data-testid="contact-message" placeholder="Your message *" rows={7} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${field} resize-none`} />
            <button type="submit" disabled={sending} data-testid="contact-submit"
              className="w-full md:w-auto bg-black text-white rounded-full px-12 py-4 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors disabled:opacity-60">
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
